import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ErrorVariant } from '@percent/domain/verifications'
import {
  Flex,
  FormField,
  Loader,
  Select,
  SelectOption,
  TextInput,
} from '@percent/lemonade'
import { Card, CountrySelect } from '@percent/product-nomination-form/ui'
import { ErrorCard } from '@percent/product-nomination-form/ui/ErrorCard/ErrorCard'
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData'

import { CustomOrgSubmitNominationFormPayload } from '@percent/product-nomination-form/api/nominationForm/nominationForm.types'
import { useProgress } from '@percent/product-nomination-form/hooks'
import { HostedFormStep } from '../../HostedForm.types'
import { RegisterOrganisationProps } from './RegisterOrganisation.types'

export const RegisterOrganisation = ({
  isLoading,
  isError,
  registries,
}: RegisterOrganisationProps) => {
  const { setCountry, country, handleNext, handleBack } =
    useValidationStepData()
  const {
    formState: { errors },
    setValue,
    register,
    control,
  } = useFormContext<CustomOrgSubmitNominationFormPayload>()
  const { t } = useTranslation()
  const { progress } = useProgress()

  const [registryName, organisationName, registryId, website] = useWatch({
    control,
    name: ['registryName', 'organisationName', 'registryId', 'website'],
  })
  const [showUnknownRegistryName, setShowUnknownRegistryName] = useState(false)

  const CANT_FIND_REGISTRY = t(
    'hostedValidation.typography.cantFindRegistryName'
  )

  const handleCountryChange = (newValue: SelectOption | null) => {
    setCountry(newValue?.value || '')
    setValue('registryName', '')
  }

  const handleNavigateNext = () => {
    return handleNext(HostedFormStep.NOMINEES)
  }

  const handleNavigateBack = () => {
    setValue('registryName', '')
    setValue('registryId', '')
    setValue('organisationName', '')
    setValue('website', '')
    handleBack()
  }

  const registryNameOptions = useMemo(
    () =>
      registries?.map((registry) => ({
        value: registry.name,
        label: registry.name,
      })) ?? [],
    [registries]
  )

  const registryNameValue = useMemo(() => {
    if (showUnknownRegistryName) {
      return { value: CANT_FIND_REGISTRY, label: CANT_FIND_REGISTRY }
    }

    return (
      registryNameOptions.find((option) => option.value === registryName) ?? {
        value: '',
        label: '',
      }
    )
  }, [CANT_FIND_REGISTRY, registryName, registryNameOptions])

  if (isError) {
    return (
      <ErrorCard
        title={t('hostedValidation.errorMessage.somethingWentWrong')}
        subtitle={t('hostedValidation.errorMessage.reloadPage')}
        variant={ErrorVariant.INTERNAL}
      />
    )
  }

  const isNextButtonDisabled =
    !country || !organisationName || !registryName || !registryId || !website

  const handleRegistryNameChange = (newValue: SelectOption | null) => {
    setValue('registryName', newValue?.value || '')
    setShowUnknownRegistryName(false)
  }

  return (
    <Card
      onBack={handleNavigateBack}
      nextButtonDisabled={isNextButtonDisabled}
      onNext={handleNavigateNext}
      nextButtonLabel={t('hostedValidation.button.next')}
      title={t('hostedValidation.title.registerOrg')}
      description={t('hostedValidation.title.registerOrgSub')}
      progress={progress}
    >
      {isLoading && <Loader />}
      {!isLoading && !isError && (
        <Flex direction="column" gap={16} w="100%">
          <FormField
            label={t('hostedValidation.label.organizationName')}
            status={errors.organisationName?.message ? 'danger' : 'default'}
            necessity="required"
            statusMessage={errors.organisationName?.message}
          >
            <TextInput
              {...register('organisationName')}
              placeholder={t('hostedValidation.placeholder.org')}
            />
          </FormField>
          <FormField
            label={t('hostedValidation.label.country')}
            necessity="required"
          >
            <CountrySelect
              placeholder={t('hostedValidation.placeholder.country')}
              value={country}
              onChange={handleCountryChange}
            />
          </FormField>
          <FormField
            label={t('hostedValidation.label.registryName')}
            necessity="required"
          >
            <Select
              placeholder={t('hostedValidation.placeholder.registryName')}
              defaultValue={registryNameValue}
              onChange={handleRegistryNameChange}
              disabled={isLoading}
              searchable
              options={registryNameOptions}
              addOption={{
                label: CANT_FIND_REGISTRY,
                onClick: () => {
                  setShowUnknownRegistryName(true)
                  setValue('registryName', '')
                  // setValue('registryName', CANT_FIND_REGISTRY)
                },
              }}
            />
          </FormField>
          {showUnknownRegistryName && (
            <FormField
              label={t('hostedValidation.label.registryName')}
              status={errors.registryName?.message ? 'danger' : 'default'}
              necessity="required"
              statusMessage={errors.registryName?.message}
            >
              <TextInput
                {...register('registryName')}
                placeholder={t('hostedValidation.placeholder.registryName')}
              />
            </FormField>
          )}
          <FormField
            label={t('hostedValidation.label.registryId')}
            status={errors.registryId?.message ? 'danger' : 'default'}
            necessity="required"
            statusMessage={errors.registryId?.message}
          >
            <TextInput
              {...register('registryId')}
              placeholder={t('hostedValidation.placeholder.registryId')}
            />
          </FormField>
          <FormField
            label={t('hostedValidation.label.website')}
            status={errors.website?.message ? 'danger' : 'default'}
            necessity="required"
            statusMessage={errors.website?.message}
          >
            <TextInput
              {...register('website')}
              placeholder={t('hostedValidation.placeholder.website')}
            />
          </FormField>
        </Flex>
      )}
    </Card>
  )
}
